/**
 * Undo/Redo hook for Todo Lists.
 *
 * Takes snapshots of the full list detail before each mutation.
 * On undo/redo, restores the snapshot to the React Query cache
 * and fires reverse API calls to sync the server.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import type { TodoListDetailResponse } from '@/lib/api';
import { useUndoRedo } from '@/lib/hooks/useUndoRedo';
import { syncToServer } from './useTodoUndoRedo.sync';

interface UseTodoUndoRedoOptions {
  listId: string;
  enabled?: boolean;
}

interface UseTodoUndoRedoReturn {
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  captureSnapshot: () => void;
  clear: () => void;
}

export function useTodoUndoRedo({
  listId,
  enabled = true,
}: UseTodoUndoRedoOptions): UseTodoUndoRedoReturn {
  const queryClient = useQueryClient();
  const detailKey = queryKeys.todoLists.detail(listId);

  const {
    canUndo,
    canRedo,
    snapshot,
    undo: undoRaw,
    redo: redoRaw,
    clear,
  } = useUndoRedo<TodoListDetailResponse>({
    maxHistory: 50,
  });

  // Track whether a mutation is pending so we can snapshot the result
  const pendingMutation = useRef(false);
  // Suppress cache subscription during undo/redo apply
  const isApplying = useRef(false);

  // Capture the initial state on first load so undo has a baseline to go back to
  const initialCaptured = useRef(false);

  // Subscribe to query cache changes to capture post-mutation states
  useEffect(() => {
    if (!enabled) return;
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (isApplying.current) return;
      if (event.type === 'updated' && event.action.type === 'success') {
        const queryKey = event.query.queryKey;
        if (JSON.stringify(queryKey) === JSON.stringify(detailKey)) {
          const data = event.query.state.data as TodoListDetailResponse | undefined;
          if (!data) return;

          if (!initialCaptured.current) {
            snapshot(data);
            initialCaptured.current = true;
            return;
          }

          if (pendingMutation.current) {
            pendingMutation.current = false;
            snapshot(data);
          }
        }
      }
    });
    return unsubscribe;
  }, [enabled, queryClient, detailKey, snapshot]);

  const captureSnapshot = useCallback(() => {
    if (!enabled) return;
    // Capture the "before" state
    const current = queryClient.getQueryData<TodoListDetailResponse>(detailKey);
    if (current) {
      snapshot(current);
    }
    // Mark that the "after" state should be captured when the cache updates
    pendingMutation.current = true;
  }, [enabled, queryClient, detailKey, snapshot]);

  const applyState = useCallback(
    async (targetState: TodoListDetailResponse | null) => {
      if (!targetState) return;

      // Get the current server-synced state before we overwrite the cache
      const currentState = queryClient.getQueryData<TodoListDetailResponse>(detailKey);

      // Suppress cache subscription during apply
      isApplying.current = true;

      // Cancel in-flight queries
      await queryClient.cancelQueries({ queryKey: detailKey });

      // Immediately restore the UI
      queryClient.setQueryData<TodoListDetailResponse>(detailKey, targetState);

      // Re-enable cache subscription
      isApplying.current = false;

      // Sync the server in the background
      if (currentState) {
        syncToServer(listId, currentState, targetState).finally(() => {
          queryClient.invalidateQueries({ queryKey: detailKey });
        });
      }
    },
    [queryClient, detailKey, listId],
  );

  const undo = useCallback(() => {
    if (!enabled) return;
    const state = undoRaw();
    applyState(state);
  }, [enabled, undoRaw, applyState]);

  const redo = useCallback(() => {
    if (!enabled) return;
    const state = redoRaw();
    applyState(state);
  }, [enabled, redoRaw, applyState]);

  const clearAll = useCallback(() => {
    clear();
    initialCaptured.current = false;
    pendingMutation.current = false;
  }, [clear]);

  return {
    canUndo: enabled && canUndo,
    canRedo: enabled && canRedo,
    undo,
    redo,
    captureSnapshot,
    clear: clearAll,
  };
}
